import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  SportsTennis,
  LocationOn,
  Business,
  EmojiEvents,
  Score,
  Settings,
  Logout,
  AccountCircle,
  Analytics,
  Notifications,
  Schedule,
  ChevronLeft,
  Flag,
  CalendarToday,
  Email,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { getConfig } from '../../config/environment';
import { getAvatarUrl, isDefaultAvatar } from '../../utils/avatar';

const drawerWidth = 280;

interface AdminLayoutProps {
  children: React.ReactNode;
}

const adminMenuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
  { text: 'Users', icon: <People />, path: '/admin/users' },
  { text: 'Matches', icon: <SportsTennis />, path: '/admin/matches' },
  { text: 'Tournaments', icon: <EmojiEvents />, path: '/admin/tournaments' },
  { text: 'Tournament Management', icon: <EmojiEvents />, path: '/admin/tournament-management' },
  { text: 'Clubs', icon: <Business />, path: '/admin/clubs' },
  { text: 'Courts', icon: <LocationOn />, path: '/admin/courts' },
  { text: 'Scores', icon: <Score />, path: '/admin/scores' },
  { text: 'Time Slots', icon: <Schedule />, path: '/admin/time-slots' },
  { text: 'Calendar', icon: <CalendarToday />, path: '/admin/calendar' },
  { text: 'Notifications', icon: <Notifications />, path: '/admin/notifications' },
  { text: 'Email Management', icon: <Email />, path: '/admin/emails' },
  { text: 'Analytics', icon: <Analytics />, path: '/admin/analytics' },
  { text: 'Feature Flags', icon: <Flag />, path: '/admin/feature-flags' },
  { text: 'System', icon: <Settings />, path: '/admin/system' },
];

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleOpenPadelSkill = async () => {
    try {
      const response = await apiService.getSsoToken();
      const { sso_token } = response.data;
      const padelSkillUrl = `${getConfig().PADELSKILL_URL}/sso?token=${sso_token}`;
      window.open(padelSkillUrl, '_blank');
      handleProfileMenuClose();
    } catch (error) {
      setSsoError(t('sso.ssoError', 'Failed to generate SSO token'));
      handleProfileMenuClose();
    }
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <img
            src="/logo-no-text.png"
            alt="PadPal Logo"
            style={{
              width: 40,
              height: 40,
              filter: 'brightness(0) invert(1)',
            }}
          />
          <Typography variant="h6" noWrap component="div" fontWeight={600}>
            PadPal Admin
          </Typography>
        </Box>
        {!isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ color: 'white' }}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ px: 1, py: 2 }}>
        {adminMenuItems.map((item) => {
          const isActive = item.path === '/admin/users'
            ? location.pathname.startsWith('/admin/users')
            : location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: `${theme.palette.primary.main}15`,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: `${theme.palette.primary.main}25`,
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.main,
                    },
                  },
                  '&:hover': {
                    backgroundColor: `${theme.palette.primary.main}08`,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? theme.palette.primary.main : 'inherit',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ mx: 2 }} />
      <List sx={{ px: 1, py: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              navigate('/');
              setMobileOpen(false);
            }}
            sx={{
              borderRadius: 2,
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}08`,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AccountCircle />
            </ListItemIcon>
            <ListItemText primary="Back to Main Site" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div" fontWeight={600}>
              Admin Panel
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {user?.is_admin && (
              <Chip
                label="Admin"
                color="primary"
                size="small"
                icon={<AccountCircle />}
              />
            )}
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ cursor: 'pointer' }}
              onClick={handleProfileMenuOpen}
            >
              <Avatar
                src={getAvatarUrl(user?.avatar_url) || undefined}
                sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
              >
                {isDefaultAvatar(user?.avatar_url) ? user?.name?.charAt(0).toUpperCase() || 'A' : null}
              </Avatar>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user?.name || 'Admin'}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleOpenPadelSkill}>
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          {t('sso.openPadelSkill', 'Open PadelSkill')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      <Snackbar
        open={!!ssoError}
        autoHideDuration={6000}
        onClose={() => setSsoError(null)}
      >
        <Alert onClose={() => setSsoError(null)} severity="error">
          {ssoError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
